'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { ArrowLeft, PlusCircle, Search, Clock, CheckCircle2, AlertCircle, X, Send, Paperclip, ChevronDown, ChevronUp,  } from 'lucide-react';
import Link from 'next/link';
import Badge from '@/components/ui/Badge';

interface TicketMessage {
  author: string;
  role: 'client' | 'support';
  message: string;
  time: string;
}

interface Ticket {
  id: string;
  subject: string;
  project: string;
  priority: string;
  priorityVariant: string;
  status: 'Open' | 'In Progress' | 'Resolved' | 'Closed';
  created: string;
  updated: string;
  category: string;
  messages: TicketMessage[];
}

const tickets: Ticket[] = [
  {
    id: 'TKT-0091',
    subject: 'Login issue on staging environment',
    project: 'Nexus ERP Platform',
    priority: 'High',
    priorityVariant: 'priority-high',
    status: 'In Progress',
    created: 'Aug 11, 2026',
    updated: '2h ago',
    category: 'Bug Report',
    messages: [
      { author: 'James Okafor', role: 'client', message: 'Users are unable to log in to the staging environment. Getting a 401 error after entering credentials.', time: 'Aug 11, 10:30 AM' },
      { author: 'Support Team', role: 'support', message: 'Thank you for reporting this. We have identified the issue — the session token expiry was set too short. We are deploying a fix now.', time: 'Aug 11, 11:45 AM' },
      { author: 'James Okafor', role: 'client', message: 'Any ETA on the fix? Our team needs access for testing today.', time: 'Aug 11, 12:00 PM' },
      { author: 'Support Team', role: 'support', message: 'Fix will be deployed within the next 2 hours. We will notify you once it is live.', time: 'Aug 11, 12:15 PM' },
    ],
  },
  {
    id: 'TKT-0087',
    subject: 'API integration not returning correct data',
    project: 'Nexus ERP Platform',
    priority: 'Critical',
    priorityVariant: 'priority-critical',
    status: 'Open',
    created: 'Aug 10, 2026',
    updated: '1d ago',
    category: 'Technical Issue',
    messages: [
      { author: 'James Okafor', role: 'client', message: 'The inventory API endpoint /api/v1/inventory is returning empty arrays even though data exists in the database. This is blocking our testing.', time: 'Aug 10, 3:00 PM' },
      { author: 'Support Team', role: 'support', message: 'We have escalated this to the backend team. Investigating now.', time: 'Aug 10, 4:30 PM' },
    ],
  },
  {
    id: 'TKT-0082',
    subject: 'Request for additional user licenses',
    project: 'Nexus ERP Platform',
    priority: 'Low',
    priorityVariant: 'priority-low',
    status: 'Resolved',
    created: 'Aug 7, 2026',
    updated: '3d ago',
    category: 'Account & Billing',
    messages: [
      { author: 'James Okafor', role: 'client', message: 'We need to add 5 more user licenses to our ERP subscription. Please advise on the process.', time: 'Aug 7, 9:00 AM' },
      { author: 'Support Team', role: 'support', message: 'We have added 5 additional licenses to your account. The updated invoice will be sent separately.', time: 'Aug 8, 10:00 AM' },
      { author: 'James Okafor', role: 'client', message: 'Thank you, confirmed the licenses are now active.', time: 'Aug 8, 11:30 AM' },
    ],
  },
  {
    id: 'TKT-0075',
    subject: 'Mobile responsiveness issues on dashboard',
    project: 'BlueStar E-Commerce Portal',
    priority: 'Medium',
    priorityVariant: 'priority-medium',
    status: 'Closed',
    created: 'Jul 28, 2026',
    updated: '2w ago',
    category: 'Bug Report',
    messages: [
      { author: 'James Okafor', role: 'client', message: 'The admin dashboard is not rendering correctly on mobile devices. Tables are overflowing.', time: 'Jul 28, 2:00 PM' },
      { author: 'Support Team', role: 'support', message: 'Fixed in the latest deployment. All tables now have horizontal scroll on mobile.', time: 'Jul 30, 4:00 PM' },
    ],
  },
];

const statusConfig = {
  Open: { color: 'text-danger', bg: 'bg-danger/10', icon: AlertCircle },
  'In Progress': { color: 'text-warning', bg: 'bg-warning/10', icon: Clock },
  Resolved: { color: 'text-success', bg: 'bg-success/10', icon: CheckCircle2 },
  Closed: { color: 'text-muted-foreground', bg: 'bg-muted', icon: CheckCircle2 },
};

const categories = ['Bug Report', 'Technical Issue', 'Account & Billing', 'Feature Request', 'General Inquiry'];
const priorities = ['Low', 'Medium', 'High', 'Critical'];

export default function ClientSupportTicketsPage() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [expandedId, setExpandedId] = useState<string | null>('TKT-0091');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [newTicket, setNewTicket] = useState({ subject: '', project: '', category: '', priority: 'Medium', description: '' });
  const [submitted, setSubmitted] = useState(false);

  const filtered = tickets.filter((t) => {
    const matchSearch = t.id.toLowerCase().includes(search.toLowerCase()) || t.subject.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'All' || t.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const handleNewTicketSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setShowNewTicket(false);
      setSubmitted(false);
      setNewTicket({ subject: '', project: '', category: '', priority: 'Medium', description: '' });
    }, 2000);
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-6 py-6 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Link href="/client-portal">
              <button className="p-2 rounded-lg border border-border hover:bg-muted/40 transition-colors text-muted-foreground hover:text-foreground">
                <ArrowLeft size={16} />
              </button>
            </Link>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Client Portal</p>
              <h1 className="text-xl font-bold text-foreground">Support Tickets</h1>
            </div>
          </div>
          <button
            onClick={() => setShowNewTicket(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <PlusCircle size={15} /> Raise Ticket
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Open', count: tickets.filter((t) => t.status === 'Open').length, color: 'text-danger', bg: 'bg-danger/10' },
            { label: 'In Progress', count: tickets.filter((t) => t.status === 'In Progress').length, color: 'text-warning', bg: 'bg-warning/10' },
            { label: 'Resolved', count: tickets.filter((t) => t.status === 'Resolved').length, color: 'text-success', bg: 'bg-success/10' },
            { label: 'Total', count: tickets.length, color: 'text-primary', bg: 'bg-primary/10' },
          ].map((s) => (
            <div key={s.label} className="card-surface p-4">
              <p className={`text-metric ${s.color}`}>{s.count}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tickets..."
              className="w-full bg-card border border-border rounded-lg pl-8 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
            />
          </div>
          <div className="flex items-center gap-2">
            {['All', 'Open', 'In Progress', 'Resolved', 'Closed'].map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStatus === s ? 'bg-primary text-white' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Ticket List */}
        <div className="space-y-3">
          {filtered.map((ticket) => {
            const isExpanded = expandedId === ticket.id;
            const cfg = statusConfig[ticket.status];
            const StatusIcon = cfg.icon;

            return (
              <div key={ticket.id} className="card-surface overflow-hidden">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : ticket.id)}
                  className="w-full p-4 flex items-center gap-4 hover:bg-muted/20 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono text-muted-foreground">{ticket.id}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}>
                        <StatusIcon size={10} /> {ticket.status}
                      </span>
                      <Badge label={ticket.priority} variant={ticket.priorityVariant as any} />
                      <span className="text-2xs text-muted-foreground bg-muted/40 px-2 py-0.5 rounded-full">{ticket.category}</span>
                    </div>
                    <p className="text-sm font-medium text-foreground">{ticket.subject}</p>
                    <p className="text-2xs text-muted-foreground mt-0.5">{ticket.project} · Updated {ticket.updated}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-2xs text-muted-foreground hidden sm:block">{ticket.messages.length} messages</span>
                    {isExpanded ? <ChevronUp size={15} className="text-muted-foreground" /> : <ChevronDown size={15} className="text-muted-foreground" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-border">
                    {/* Messages */}
                    <div className="p-4 space-y-3 max-h-80 overflow-y-auto scrollbar-thin">
                      {ticket.messages.map((msg, idx) => (
                        <div key={idx} className={`flex gap-3 ${msg.role === 'client' ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${msg.role === 'client' ? 'bg-primary/20 text-primary' : 'bg-cyan/20 text-cyan'}`}>
                            {msg.author.charAt(0)}
                          </div>
                          <div className={`max-w-[75%] ${msg.role === 'client' ? 'items-end' : 'items-start'} flex flex-col`}>
                            <div className={`rounded-xl px-3 py-2 text-sm ${msg.role === 'client' ? 'bg-primary/10 text-foreground rounded-tr-sm' : 'bg-muted/40 text-foreground rounded-tl-sm'}`}>
                              {msg.message}
                            </div>
                            <p className="text-2xs text-muted-foreground mt-1">{msg.author} · {msg.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Reply */}
                    {ticket.status !== 'Closed' && (
                      <div className="border-t border-border p-4">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={replyText[ticket.id] || ''}
                            onChange={(e) => setReplyText((r) => ({ ...r, [ticket.id]: e.target.value }))}
                            placeholder="Type your reply..."
                            className="flex-1 bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                          />
                          <button className="p-2 rounded-lg border border-border hover:bg-muted/40 transition-colors text-muted-foreground hover:text-foreground">
                            <Paperclip size={15} />
                          </button>
                          <button
                            disabled={!replyText[ticket.id]}
                            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Send size={13} /> Send
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* New Ticket Modal */}
        {showNewTicket && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="card-elevated w-full max-w-lg">
              <div className="flex items-center justify-between p-5 border-b border-border">
                <h2 className="text-base font-bold text-foreground">Raise a Support Ticket</h2>
                <button onClick={() => setShowNewTicket(false)} className="p-2 rounded-lg hover:bg-muted/40 transition-colors text-muted-foreground hover:text-foreground">
                  <X size={16} />
                </button>
              </div>
              {submitted ? (
                <div className="p-8 text-center">
                  <CheckCircle2 size={40} className="text-success mx-auto mb-3" />
                  <p className="text-sm font-semibold text-foreground">Ticket submitted successfully!</p>
                  <p className="text-xs text-muted-foreground mt-1">Our support team will respond within 4 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleNewTicketSubmit} className="p-5 space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Subject *</label>
                    <input
                      type="text"
                      value={newTicket.subject}
                      onChange={(e) => setNewTicket((f) => ({ ...f, subject: e.target.value }))}
                      placeholder="Brief description of the issue"
                      className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5">Category</label>
                      <select
                        value={newTicket.category}
                        onChange={(e) => setNewTicket((f) => ({ ...f, category: e.target.value }))}
                        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                      >
                        <option value="">Select category</option>
                        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5">Priority</label>
                      <select
                        value={newTicket.priority}
                        onChange={(e) => setNewTicket((f) => ({ ...f, priority: e.target.value }))}
                        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                      >
                        {priorities.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Related Project</label>
                    <select
                      value={newTicket.project}
                      onChange={(e) => setNewTicket((f) => ({ ...f, project: e.target.value }))}
                      className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                    >
                      <option value="">Select project (optional)</option>
                      <option>Nexus ERP Platform</option>
                      <option>BlueStar E-Commerce Portal</option>
                      <option>Meridian Cloud Migration</option>
                      <option>General / Not project-specific</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Description *</label>
                    <textarea
                      value={newTicket.description}
                      onChange={(e) => setNewTicket((f) => ({ ...f, description: e.target.value }))}
                      rows={4}
                      placeholder="Describe the issue in detail. Include steps to reproduce if applicable..."
                      className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors resize-none"
                    />
                  </div>
                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => setShowNewTicket(false)}
                      className="flex-1 px-4 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/40 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!newTicket.subject || !newTicket.description}
                      className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Submit Ticket
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
